import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import { toast } from 'sonner';
import { 
  FileText, Save, RotateCcw, Check, AlertCircle, 
  Home, Info, Phone, Layout, ExternalLink, Eye
} from 'lucide-react';

const API = '/api';

// Section icons mapping
const SECTION_ICONS = {
  'home_hero': Home,
  'home_why_wedo': Layout,
  'about_overview': Info,
  'about_mission_vision': Info,
  'about_values': Info,
  'contact_info': Phone,
  'footer': FileText,
};

// Group sections by page
const PAGE_GROUPS = {
  'Home Page': ['home_hero', 'home_why_wedo'],
  'About Page': ['about_overview', 'about_mission_vision', 'about_values'],
  'Contact & Footer': ['contact_info', 'footer'],
};

export const AdminCMSPageContentPage = () => {
  const { language } = useLanguage();
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingSection, setEditingSection] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [sectionToReset, setSectionToReset] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    content_en: {},
    content_ar: {},
    is_active: true,
  });

  useEffect(() => {
    fetchSections();
  }, []);

  const fetchSections = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/admin/cms/page-content`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSections(data);
      }
    } catch (error) {
      console.error('Error fetching page content:', error);
      toast.error('Failed to load page content');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (section) => {
    setEditingSection(section);
    setFormData({
      content_en: { ...section.content_en },
      content_ar: { ...section.content_ar },
      is_active: section.is_active !== false,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!editingSection) return;
    
    setSaving(true);
    try {
      const response = await fetch(`${API}/admin/cms/page-content/${editingSection.section_key}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(language === 'ar' ? 'تم الحفظ بنجاح' : 'Content saved successfully');
        setDialogOpen(false);
        fetchSections();
      } else {
        toast.error('Failed to save content');
      }
    } catch (error) {
      console.error('Error saving content:', error);
      toast.error('Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!sectionToReset) return;
    
    try {
      const response = await fetch(`${API}/admin/cms/page-content/${sectionToReset.section_key}/reset`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.ok) {
        toast.success(language === 'ar' ? 'تمت إعادة التعيين' : 'Reset to defaults');
        setResetDialogOpen(false);
        setSectionToReset(null);
        fetchSections();
      } else {
        toast.error('Failed to reset');
      }
    } catch (error) {
      console.error('Error resetting:', error);
      toast.error('Failed to reset');
    }
  };

  const updateEnField = (field, value) => {
    setFormData(prev => ({
      ...prev,
      content_en: { ...prev.content_en, [field]: value }
    }));
  };

  const updateArField = (field, value) => {
    setFormData(prev => ({
      ...prev,
      content_ar: { ...prev.content_ar, [field]: value }
    }));
  };

  const renderFieldInput = (field, langSuffix, isArabic = false) => {
    const value = isArabic ? formData.content_ar[field] : formData.content_en[field];
    const updateFn = isArabic ? updateArField : updateEnField;
    
    // Determine if field should be textarea (long text fields)
    const isLongText = field.includes('text') || field.includes('desc') || field.includes('subtitle') || field.includes('description');
    
    const fieldLabel = field
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());

    return (
      <div key={`${field}-${langSuffix}`} className="space-y-1">
        <Label className="text-sm">{fieldLabel}</Label>
        {isLongText ? (
          <Textarea
            value={value || ''}
            onChange={(e) => updateFn(field, e.target.value)}
            rows={3}
            dir={isArabic ? 'rtl' : 'ltr'}
            className="text-sm"
            data-testid={`field-${field}-${langSuffix}`}
          />
        ) : (
          <Input
            value={value || ''}
            onChange={(e) => updateFn(field, e.target.value)}
            dir={isArabic ? 'rtl' : 'ltr'}
            className="text-sm"
            data-testid={`field-${field}-${langSuffix}`}
          />
        )}
      </div>
    );
  };

  const getSectionsByGroup = (groupKeys) => {
    return sections.filter(s => groupKeys.includes(s.section_key));
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#111827]">
            {language === 'ar' ? 'محتوى الصفحات' : 'Page Content'}
          </h1>
          <p className="text-[#6B7280] mt-1">
            {language === 'ar' 
              ? 'تعديل النصوص والمحتوى على الصفحات العامة'
              : 'Edit text and content on public pages'}
          </p>
        </div>
        <Button 
          variant="outline" 
          onClick={() => window.open('/', '_blank')}
          className="gap-2"
        >
          <Eye className="h-4 w-4" />
          {language === 'ar' ? 'معاينة الموقع' : 'Preview Site'}
          <ExternalLink className="h-3 w-3" />
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-8 text-[#6B7280]">Loading...</div>
      ) : (
        <Accordion type="multiple" defaultValue={Object.keys(PAGE_GROUPS)} className="space-y-4">
          {Object.entries(PAGE_GROUPS).map(([groupName, groupKeys]) => (
            <AccordionItem key={groupName} value={groupName} className="border rounded-lg overflow-hidden">
              <AccordionTrigger className="px-4 py-3 bg-gray-50 hover:bg-gray-100">
                <span className="font-semibold text-[#111827]">
                  {language === 'ar' ? getArabicGroupName(groupName) : groupName}
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-4 py-3">
                <div className="space-y-3">
                  {getSectionsByGroup(groupKeys).map((section) => {
                    const Icon = SECTION_ICONS[section.section_key] || FileText;
                    const isCustomized = !section.is_default;
                    
                    return (
                      <Card 
                        key={section.section_key}
                        className={`transition-all ${!section.is_active ? 'opacity-60 bg-gray-50' : ''}`}
                        data-testid={`section-card-${section.section_key}`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="h-10 w-10 rounded-lg bg-[#33AADD]/10 flex items-center justify-center">
                                <Icon className="h-5 w-5 text-[#33AADD]" />
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <h3 className="font-medium text-[#111827]">
                                    {language === 'ar' ? section.label_ar : section.label_en}
                                  </h3>
                                  {isCustomized && (
                                    <Badge variant="secondary" className="text-xs bg-[#33AADD]/10 text-[#33AADD]">
                                      {language === 'ar' ? 'مخصص' : 'Customized'}
                                    </Badge>
                                  )}
                                  {!section.is_active && (
                                    <Badge variant="secondary" className="text-xs">
                                      {language === 'ar' ? 'مخفي' : 'Hidden'}
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-xs text-[#6B7280] mt-0.5">
                                  {section.fields?.length || 0} {language === 'ar' ? 'حقول' : 'fields'}
                                </p>
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-2">
                              {isCustomized && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setSectionToReset(section);
                                    setResetDialogOpen(true);
                                  }}
                                  title={language === 'ar' ? 'إعادة تعيين' : 'Reset to defaults'}
                                >
                                  <RotateCcw className="h-4 w-4" />
                                </Button>
                              )}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleOpenDialog(section)}
                                className="gap-2"
                                data-testid={`edit-section-${section.section_key}`}
                              >
                                <FileText className="h-4 w-4" />
                                {language === 'ar' ? 'تعديل' : 'Edit'}
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}

      {/* Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {language === 'ar' ? editingSection?.label_ar : editingSection?.label_en}
            </DialogTitle>
            <DialogDescription>
              {language === 'ar' 
                ? 'قم بتعديل المحتوى بالإنجليزية والعربية'
                : 'Edit content in English and Arabic'}
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="english" className="mt-4">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="english">
                {language === 'ar' ? 'الإنجليزية' : 'English'}
              </TabsTrigger>
              <TabsTrigger value="arabic">
                {language === 'ar' ? 'العربية' : 'Arabic'}
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="english" className="mt-4">
              <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                {editingSection?.fields?.map(field => renderFieldInput(field, 'en', false))}
              </div>
            </TabsContent>
            
            <TabsContent value="arabic" className="mt-4">
              <div className="space-y-4 p-4 bg-gray-50 rounded-lg" dir="rtl">
                {editingSection?.fields?.map(field => renderFieldInput(field, 'ar', true))}
              </div>
            </TabsContent>
          </Tabs>

          {/* Active Toggle */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg mt-4">
            <div>
              <Label>{language === 'ar' ? 'نشط' : 'Active'}</Label>
              <p className="text-xs text-[#6B7280]">
                {language === 'ar' 
                  ? 'عند التعطيل، سيتم استخدام المحتوى الافتراضي'
                  : 'When disabled, default content will be used'}
              </p>
            </div>
            <Switch
              checked={formData.is_active}
              onCheckedChange={(checked) => setFormData({...formData, is_active: checked})}
            />
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              {language === 'ar' ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="bg-[#33AADD] hover:bg-[#2b8eb8] gap-2"
            >
              {saving ? (
                <>Saving...</>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  {language === 'ar' ? 'حفظ التغييرات' : 'Save Changes'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Confirmation Dialog */}
      <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-500" />
              {language === 'ar' ? 'إعادة تعيين إلى الافتراضي' : 'Reset to Default'}
            </DialogTitle>
            <DialogDescription>
              {language === 'ar' 
                ? `هل أنت متأكد من إعادة تعيين "${sectionToReset?.label_ar}" إلى المحتوى الافتراضي؟ سيتم فقدان جميع التغييرات.`
                : `Are you sure you want to reset "${sectionToReset?.label_en}" to default content? All customizations will be lost.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetDialogOpen(false)}>
              {language === 'ar' ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleReset}
              className="gap-2"
            >
              <RotateCcw className="h-4 w-4" />
              {language === 'ar' ? 'إعادة تعيين' : 'Reset'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Helper for Arabic group names
const getArabicGroupName = (groupName) => {
  const map = {
    'Home Page': 'الصفحة الرئيسية',
    'About Page': 'صفحة من نحن',
    'Contact & Footer': 'الاتصال والتذييل',
  };
  return map[groupName] || groupName;
};

export default AdminCMSPageContentPage;
